import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, FileSpreadsheet, Globe, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { StatementFormat } from '@/types/statement';

interface StatementFormatDialogProps {
    open: boolean;
    onClose: () => void;
    onFormatSelected: (format: StatementFormat) => Promise<void>;
    isGenerating: boolean;
}

const formatOptions: {
    value: StatementFormat;
    labelKey: string;
    descKey: string;
    icon: typeof FileText;
}[] = [
    { value: 'pdf', labelKey: 'formatPdf', descKey: 'formatPdfDesc', icon: FileText },
    { value: 'excel', labelKey: 'formatExcel', descKey: 'formatExcelDesc', icon: FileSpreadsheet },
    { value: 'html', labelKey: 'formatHtml', descKey: 'formatHtmlDesc', icon: Globe },
];

const StatementFormatDialog: React.FC<StatementFormatDialogProps> = ({
    open,
    onClose,
    onFormatSelected,
    isGenerating,
}) => {
    const { t } = useTranslation('mobile');
    const [selectedFormat, setSelectedFormat] = useState<StatementFormat>('pdf');

    const handleDownload = async () => {
        await onFormatSelected(selectedFormat);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t('downloadStatement')}</DialogTitle>
                    <DialogDescription>{t('selectStatementFormat')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-4">
                    {formatOptions.map(option => {
                        const Icon = option.icon;
                        return (
                            <div
                                key={option.value}
                                className={`relative flex items-start space-x-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                                    selectedFormat === option.value
                                        ? 'border-primary bg-primary/5'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                                onClick={() => !isGenerating && setSelectedFormat(option.value)}
                            >
                                <div className="flex items-center h-5">
                                    <input
                                        type="radio"
                                        name="format"
                                        value={option.value}
                                        checked={selectedFormat === option.value}
                                        onChange={() => setSelectedFormat(option.value)}
                                        disabled={isGenerating}
                                        className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <Icon className="h-5 w-5 text-gray-600" />
                                        <label className="font-medium text-sm cursor-pointer">
                                            {t(option.labelKey)}
                                        </label>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {t(option.descKey)}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isGenerating}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleDownload} disabled={isGenerating}>
                        {isGenerating ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('generating')}
                            </>
                        ) : (
                            t('download')
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default StatementFormatDialog;
